import { useEffect, useState } from "react";
import { Mail, MessageSquare, CalendarCheck, StickyNote, Send, User, GitBranch, ShieldCheck, Clock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { STAGE_LABEL, REPLY_LABEL } from "@/lib/constants";
import api from "@/lib/api";

const ACT_ICON = { email_sent: Send, reply: MessageSquare, meeting: CalendarCheck, note: StickyNote, enrolled: Mail };

function when(at) {
  try { return new Date(at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); } catch { return at; }
}

export function ContactDetail({ contactId, open, onOpenChange }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!open || !contactId) { setD(null); return; }
    api.get(`/contacts/${contactId}`).then((r) => setD(r.data)).catch(() => setD(null));
  }, [open, contactId]);

  const c = d?.contact;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="contact-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {c ? c.name : "Contact"}
            {c && d.in_sequence && <Badge className="bg-amber-500/15 text-amber-500 border-0 text-[10px]" data-testid="contact-in-sequence">In sequence</Badge>}
          </DialogTitle>
          <DialogDescription className="font-mono text-xs">{c ? `${c.title || "-"} · ${c.company || "-"}` : "Loading contact…"}</DialogDescription>
        </DialogHeader>
        {c && <ContactBody d={d} />}
      </DialogContent>
    </Dialog>
  );
}

export function ContactBody({ d }) {
  const c = d?.contact;
  if (!c) return null;
  return (
          <>
            <Tabs defaultValue="overview">
              <TabsList className="grid grid-cols-5 w-full" data-testid="contact-tabs">
                <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
                <TabsTrigger value="timeline" data-testid="tab-timeline">Timeline</TabsTrigger>
                <TabsTrigger value="emails" data-testid="tab-emails">Emails</TabsTrigger>
                <TabsTrigger value="replies" data-testid="tab-replies">Replies</TabsTrigger>
                <TabsTrigger value="deal" data-testid="tab-deal">Deal</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="space-y-3 pt-2" data-testid="contact-overview">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <Field icon={User} label="Email" value={c.email || "-"} extra={c.email_status === "verified" ? <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" /> : c.email_status === "unverified" ? <span className="text-[10px] text-amber-500">unverified</span> : c.email_status === "unknown" ? <span className="text-[10px] text-rose-500">no-send</span> : null} />
                  <Field icon={GitBranch} label="Stage" value={STAGE_LABEL[c.stage] || c.stage} />
                  <Field label="Source" value={c.source} />
                  <Field label="Fit score" value={c.fit_score} />
                  <Field icon={Clock} label="Last touched" value={c.last_touched_at ? when(c.last_touched_at) : "Never"} />
                  <Field label="Do not contact" value={c.do_not_contact ? "Yes (suppressed)" : "No"} />
                </div>
                {c.source_url && <a href={c.source_url} target="_blank" rel="noreferrer" className="text-xs text-amber-500 hover:underline break-all">{c.source_url}</a>}
              </TabsContent>

              <TabsContent value="timeline" className="pt-2" data-testid="contact-timeline">
                {d.timeline.length ? (
                  <div className="space-y-3">
                    {d.timeline.map((a, i) => { const Ic = ACT_ICON[a.type] || MessageSquare; return (
                      <div key={`${a.at || a.type}-${i}`} className="flex items-start gap-3 text-sm">
                        <span className="mt-0.5 h-6 w-6 rounded-full bg-accent/60 flex items-center justify-center shrink-0"><Ic className="h-3.5 w-3.5 text-muted-foreground" /></span>
                        <div><div>{a.label}</div><div className="text-[10px] font-mono text-muted-foreground">{when(a.at)}</div></div>
                      </div>
                    ); })}
                  </div>
                ) : <Empty text="No activity yet. Enroll this contact in a sequence to start the timeline." />}
              </TabsContent>

              <TabsContent value="emails" className="pt-2 space-y-2" data-testid="contact-emails">
                {d.emails.length ? d.emails.map((e) => (
                  <div key={e.id} className="rounded-lg border border-border/60 p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">{e.draft_subject || "(no subject)"}</span>
                      <Badge variant="outline" className="text-[10px] capitalize">{e.status?.replace(/_/g, " ")}</Badge>
                    </div>
                    <div className="text-[10px] font-mono text-muted-foreground mt-0.5">{e.campaign_name || "sequence"}{e.sent_at ? ` · sent ${when(e.sent_at)}` : ""}{e.sent_via ? ` · ${e.sent_via}` : ""}</div>
                    {e.draft_body && <p className="text-xs text-muted-foreground mt-2 whitespace-pre-wrap line-clamp-4">{e.draft_body}</p>}
                  </div>
                )) : <Empty text="No emails yet. Enroll and approve a first-touch to send." />}
              </TabsContent>

              <TabsContent value="replies" className="pt-2 space-y-2" data-testid="contact-replies">
                {d.replies.length ? d.replies.map((r) => (
                  <div key={r.id} className="rounded-lg border border-border/60 p-3">
                    <div className="flex items-center gap-2 mb-1">
                      {r.category && <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded ${(REPLY_LABEL[r.category] || {}).color}`}>{(REPLY_LABEL[r.category] || {}).label}</span>}
                      <span className="text-[10px] font-mono text-muted-foreground">{when(r.received_at)}</span>
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{r.body}</p>
                    {r.summary && <p className="text-xs text-muted-foreground mt-1.5">{r.summary}</p>}
                  </div>
                )) : <Empty text="No replies logged for this contact." />}
              </TabsContent>

              <TabsContent value="deal" className="pt-2" data-testid="contact-deal">
                {d.deal ? (
                  <div className="rounded-lg border border-border/60 p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{d.deal.company || d.deal.name}</span>
                      <Badge className="bg-amber-500/15 text-amber-500 border-0">{STAGE_LABEL[d.deal.stage] || d.deal.stage}</Badge>
                    </div>
                    <div className="text-sm text-muted-foreground">Value: <span className="text-amber-500 font-medium">${(d.deal.value || 0).toLocaleString()}</span> · Health: <span className="capitalize">{d.deal.health || "-"}</span></div>
                    {(d.deal.won_reason || d.deal.lost_reason) && <div className="text-xs text-muted-foreground">Close note: {d.deal.won_reason || d.deal.lost_reason}</div>}
                  </div>
                ) : <Empty text="No deal on the board yet. Create one from Leads or the Pipeline." />}
              </TabsContent>
            </Tabs>
          </>
  );
}

function Field({ icon: Icon, label, value, extra }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground flex items-center gap-1">{Icon && <Icon className="h-3 w-3" />}{label}</div>
      <div className="text-sm mt-0.5 flex items-center gap-1.5 break-all">{value}{extra}</div>
    </div>
  );
}

function Empty({ text }) {
  return <div className="text-sm text-muted-foreground text-center py-8 border border-dashed border-border/60 rounded-lg">{text}</div>;
}
